'use client';

import { useState } from 'react';
import { motion } from 'framer-motion';
import { useMutation } from 'convex/react';
import { api } from '@convex/_generated/api';
import { Button } from '@/components/ui/button';
import { Navbar, Footer } from '@/components/home';
import {
  CheckCircle2,
  ArrowRight,
  User,
  Mail,
  Phone,
  GraduationCap,
  ArrowLeft,
} from 'lucide-react';
import { useRouter } from 'next/navigation';
import { useTranslations, useLocale } from '@/i18n';

const fadeInUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-100px' },
  transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
};

export default function WaitlistPage() {
  const router = useRouter();
  const submit = useMutation(api.academyWaitlist.submit);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    track: '',
    message: '',
  });
  const [error, setError] = useState('');
  const t = useTranslations();
  const locale = useLocale();

  const waitlist = t.waitlist;

  const tracks = [
    {
      id: 'open-community',
      name: waitlist.openCommunityTrack,
      description: waitlist.openCommunityDesc,
    },
    {
      id: 'exclusive-cohort',
      name: waitlist.exclusiveCohortTrack,
      description: waitlist.exclusiveCohortDesc,
    },
  ];

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      await submit(formData);
      setIsSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err: unknown) {
      const raw = err instanceof Error ? err.message : String(err);
      const match = raw.match(/Uncaught Error: ([^\n]+)/);
      const core = match ? match[1].trim() : "";
      if (core.includes("already on the waitlist")) setError("This email is already on the waitlist.");
      else if (core.includes("Too many submissions")) setError("Too many submissions. Please try again later.");
      else setError(t.common.error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <main className="min-h-screen bg-[#F8F7F4] dark:bg-black">
        <Navbar locale={locale} translations={t.nav} />

        <section className="min-h-[80vh] flex items-center justify-center px-6 py-20">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            className="max-w-2xl w-full text-center"
          >
            <div className="mb-8 flex justify-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                className="h-24 w-24 bg-brand-gold/20 rounded-full flex items-center justify-center"
              >
                <CheckCircle2 className="h-12 w-12 text-brand-gold" />
              </motion.div>
            </div>

            <h1 className="text-5xl md:text-7xl font-display font-bold mb-6 text-black dark:text-white leading-[0.9]">
              {waitlist.successTitle}{' '}
              <span className="text-brand-gold font-serif italic">
                {waitlist.successHighlight}
              </span>
            </h1>

            <p className="text-xl md:text-2xl text-zinc-600 dark:text-zinc-200 mb-12 font-light leading-relaxed">
              {waitlist.successMessage}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => router.push(`/${locale}/academy`)}
                className="h-14 px-8 rounded-full bg-brand-red hover:bg-brand-red/90 text-white text-base font-bold shadow-lg"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                {waitlist.backToSchool}
              </Button>
              <Button
                variant="outline"
                onClick={() => (window.location.href = `/${locale}`)}
                className="h-14 px-8 rounded-full border-black/10 dark:border-white/10 text-black dark:text-white text-base"
              >
                {waitlist.homePage}
              </Button>
            </div>
          </motion.div>
        </section>

        <Footer />
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#F8F7F4] dark:bg-black">
      <Navbar locale={locale} translations={t.nav} />

      {/* Hero Section */}
      <section className="relative py-24 px-6 overflow-hidden">
        {/* Background Elements */}
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-brand-red/5 dark:bg-brand-red/5 rounded-full blur-[150px] -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-brand-gold/5 dark:bg-brand-gold/5 rounded-full blur-[100px] translate-y-1/2 -translate-x-1/2" />

        <div className="container max-w-4xl mx-auto relative z-10">
          <motion.div
            variants={fadeInUp}
            initial="initial"
            whileInView="whileInView"
            className="text-center mb-16"
          >
            <h1 className="text-5xl md:text-7xl font-display font-bold mb-8 text-black dark:text-white leading-[0.9]">
              {waitlist.joinThe}{' '}
              <span className="text-brand-red font-serif italic">
                {waitlist.waitlist}
              </span>
            </h1>

            <p className="text-xl md:text-2xl text-zinc-600 dark:text-zinc-200 max-w-2xl mx-auto font-light leading-relaxed">
              {waitlist.description}
            </p>
          </motion.div>

          {/* Form Section */}
          <motion.div
            variants={fadeInUp}
            initial="initial"
            whileInView="whileInView"
            transition={{ delay: 0.2 }}
            className="max-w-2xl mx-auto"
          >
            <form
              onSubmit={handleSubmit}
              className="bg-white dark:bg-zinc-900 rounded-[3rem] p-8 md:p-12 shadow-xl border border-black/5 dark:border-white/5"
            >
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-400" />
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    placeholder={waitlist.firstName}
                    required
                    className="w-full h-14 pl-12 pr-4 rounded-2xl bg-[#F8F7F4] dark:bg-black border border-black/10 dark:border-white/10 focus:border-brand-red dark:focus:border-brand-gold focus:outline-none transition-colors text-black dark:text-white placeholder-zinc-500"
                  />
                </div>

                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-400" />
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    placeholder={waitlist.lastName}
                    required
                    className="w-full h-14 pl-12 pr-4 rounded-2xl bg-[#F8F7F4] dark:bg-black border border-black/10 dark:border-white/10 focus:border-brand-red dark:focus:border-brand-gold focus:outline-none transition-colors text-black dark:text-white placeholder-zinc-500"
                  />
                </div>
              </div>

              <div className="relative mb-6">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-400" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder={waitlist.email}
                  required
                  className="w-full h-14 pl-12 pr-4 rounded-2xl bg-[#F8F7F4] dark:bg-black border border-black/10 dark:border-white/10 focus:border-brand-red dark:focus:border-brand-gold focus:outline-none transition-colors text-black dark:text-white placeholder-zinc-500"
                />
              </div>

              <div className="relative mb-6">
                <Phone className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-400" />
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder={waitlist.phone}
                  className="w-full h-14 pl-12 pr-4 rounded-2xl bg-[#F8F7F4] dark:bg-black border border-black/10 dark:border-white/10 focus:border-brand-red dark:focus:border-brand-gold focus:outline-none transition-colors text-black dark:text-white placeholder-zinc-500"
                />
              </div>

              <div className="relative mb-6">
                <GraduationCap className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-400" />
                <select
                  name="track"
                  value={formData.track}
                  onChange={handleInputChange}
                  required
                  className="w-full h-14 pl-12 pr-4 rounded-2xl bg-[#F8F7F4] dark:bg-black border border-black/10 dark:border-white/10 focus:border-brand-red dark:focus:border-brand-gold focus:outline-none transition-colors text-black dark:text-white appearance-none cursor-pointer"
                >
                  <option value="">{waitlist.selectTrack}</option>
                  {tracks.map((track) => (
                    <option key={track.id} value={track.id}>
                      {track.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mb-8">
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder={waitlist.message}
                  rows={4}
                  className="w-full p-4 rounded-2xl bg-[#F8F7F4] dark:bg-black border border-black/10 dark:border-white/10 focus:border-brand-red dark:focus:border-brand-gold focus:outline-none transition-colors text-black dark:text-white placeholder-zinc-500 resize-none"
                />
              </div>

              {error && (
                <div className="mb-6 p-4 bg-red/10 dark:bg-red/20 rounded-2xl border border-red/20 dark:border-red/30">
                  <p className="text-red dark:text-red-400 text-sm">{error}</p>
                </div>
              )}

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full h-14 rounded-full bg-brand-red hover:bg-brand-red/90 text-white text-base font-bold shadow-lg shadow-brand-red/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{
                        duration: 1,
                        repeat: Infinity,
                        ease: 'linear',
                      }}
                      className="h-5 w-5 border-2 border-white/30 border-t-white rounded-full"
                    />
                    {waitlist.submitting}
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    {waitlist.joinWaitlist}
                    <ArrowRight className="h-4 w-4" />
                  </span>
                )}
              </Button>

              <p className="text-xs text-zinc-500 dark:text-zinc-400 text-center mt-6">
                {waitlist.privacyNote}
              </p>
            </form>
          </motion.div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
